import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import cytoscape, { Core, EventObject, NodeSingular } from 'cytoscape';
import '../../core/cytoscape-setup';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { GraphPlayground, PlaygroundGraph, PlaygroundNode } from '../../core/services/graph-playground';

const TYPE_COLORS: Record<string, string> = {
  Concept: '#2a78d6',
  Feature: '#eb6834',
  Product: '#1baf7a',
  Role: '#eda100',
  Organization: '#e87ba4',
  Process: '#008300',
  Person: '#4a3aa7',
  Regulation: '#e34948'
};
const OTHER_COLOR = '#898781';
const EDGE_DEFAULT = '#c3c2b7';

const FOCUS = { selected: '#2a78d6', neighbor: '#0ca30c', edge: '#0ca30c', dim: '#d8d6ce' };
const QUERY = { searching: '#fab219', seed: '#2a78d6', context: '#1baf7a', edge: '#1baf7a', dim: '#d8d6ce' };

const MAX_LABEL_LENGTH = 22;

type AskStatus = 'idle' | 'searching' | 'answering' | 'done' | 'error';

function colorForType(type: string): string {
  return TYPE_COLORS[type] || OTHER_COLOR;
}

function truncate(text: string, max = MAX_LABEL_LENGTH): string {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

@Component({
  selector: 'app-graph-playground',
  standalone: true,
  imports: [CommonModule, FormsModule, Sidebar, Navbar, EmptyState],
  templateUrl: './graph-playground.html',
  styleUrl: './graph-playground.css'
})
export class GraphPlaygroundPage implements OnInit, AfterViewInit, OnDestroy {
  private playgroundService = inject(GraphPlayground);

  @ViewChild('graphCanvas') graphCanvas?: ElementRef<HTMLDivElement>;

  private cy: Core | null = null;
  private graphData: PlaygroundGraph | null = null;
  private askTimers: ReturnType<typeof setTimeout>[] = [];
  private askRequestToken = 0;

  isLoading = signal(true);
  loadError = signal<string | null>(null);
  nodeCount = signal(0);
  edgeCount = signal(0);

  activeTypes = signal<Set<string>>(new Set());
  typeCounts = signal<Array<[string, number]>>([]);

  focusNodeId = signal<string | null>(null);
  focusedNode = signal<PlaygroundNode | null>(null);
  outgoingConnections = signal<Array<{ id: string; relationship: string; targetId: string; targetName: string }>>([]);
  incomingConnections = signal<Array<{ id: string; relationship: string; sourceId: string; sourceName: string }>>([]);

  searchQuery = signal('');
  searchResults = signal<PlaygroundNode[]>([]);
  searchOpen = signal(false);
  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  question = signal('');
  askStatus = signal<AskStatus>('idle');
  askAnswer = signal('');
  askError = signal<string | null>(null);
  askIsLocalPreview = signal(false);
  queryActive = signal(false);

  readonly askBusy = computed(() => this.askStatus() === 'searching' || this.askStatus() === 'answering');

  colorForType = colorForType;

  ngOnInit(): void {
    this.playgroundService.getGraph().subscribe({
      next: (graph) => {
        this.graphData = graph;
        this.isLoading.set(false);
        this.nodeCount.set(graph.nodes.length);
        this.edgeCount.set(graph.edges.length);
        this.activeTypes.set(new Set(graph.nodes.map(n => n.type || 'Unknown')));
        this.typeCounts.set(this.computeTypeCounts(graph));
        this.renderGraph();
      },
      error: () => {
        this.isLoading.set(false);
        this.loadError.set('Failed to load the graph dataset.');
      }
    });
  }

  ngAfterViewInit(): void {
    this.initCytoscape();
    if (this.graphData) this.renderGraph();
  }

  ngOnDestroy(): void {
    this.cancelAsk();
    this.cy?.destroy();
    this.cy = null;
  }

  private computeTypeCounts(graph: PlaygroundGraph): Array<[string, number]> {
    const counts = new Map<string, number>();
    graph.nodes.forEach(n => {
      const t = n.type || 'Unknown';
      counts.set(t, (counts.get(t) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }

  private nodeById(id: string): PlaygroundNode | undefined {
    return this.graphData?.nodes.find(n => n.id === id);
  }

  private initCytoscape() {
    const container = this.graphCanvas?.nativeElement;
    if (!container || this.cy) return;

    this.cy = cytoscape({
      container,
      elements: [],
      style: [
        {
          selector: 'node',
          style: {
            'background-color': 'data(color)',
            'border-width': 0,
            'label': 'data(label)',
            'font-family': 'Poppins, sans-serif',
            'font-size': '9px',
            'color': '#0D1121',
            'text-valign': 'bottom',
            'text-margin-y': 4,
            // An outline alone doesn't reliably hide edge lines/arrowheads
            // passing directly behind a label (a node with many connections
            // has lines radiating in every direction, including straight
            // through the label below it) — an opaque background box behind
            // the text does, regardless of how many lines cross that spot.
            'text-background-color': '#FAFBFF',
            'text-background-opacity': 1,
            'text-background-shape': 'roundrectangle',
            'text-background-padding': '3px',
            'width': 20,
            'height': 20,
            'shape': 'ellipse'
          }
        },
        { selector: 'node.hidden-type', style: { display: 'none' } },
        {
          selector: 'node.node-dim',
          style: { 'background-color': FOCUS.dim, label: '', 'z-index': 1 }
        },
        {
          // No canvas label here — the HTML badge above the graph shows the
          // focused node's name instead, since a hub node's own label can't
          // reliably stay legible through a dozen-plus radiating edges.
          selector: 'node.node-focused',
          style: {
            'background-color': FOCUS.selected,
            width: 32,
            height: 32,
            'border-width': 2,
            'border-color': '#ffffff',
            label: '',
            'z-index': 999
          }
        },
        {
          selector: 'node.node-neighbor',
          style: { 'background-color': FOCUS.neighbor, width: 26, height: 26, 'z-index': 5 }
        },
        {
          selector: 'node.query-seed',
          style: {
            'background-color': QUERY.seed,
            width: 30,
            height: 30,
            'border-width': 2,
            'border-color': '#ffffff',
            'z-index': 10
          }
        },
        {
          selector: 'node.query-context',
          style: { 'background-color': QUERY.context, width: 24, height: 24, 'z-index': 5 }
        },
        {
          selector: 'node.query-visiting',
          style: { 'background-color': QUERY.searching, width: 24, height: 24, 'z-index': 6 }
        },
        {
          selector: 'node.query-dim',
          style: { 'background-color': QUERY.dim, label: '', 'z-index': 1 }
        },
        {
          // No relationship label on the canvas itself — with dozens of edges
          // converging on one focused node, text on every edge clutters and
          // overlaps the node's own name (see the Outgoing/Incoming lists in
          // the side panel for relationship names instead).
          selector: 'edge',
          style: {
            width: 1,
            'line-color': EDGE_DEFAULT,
            opacity: 0.55,
            'target-arrow-color': EDGE_DEFAULT,
            'target-arrow-shape': 'triangle',
            'arrow-scale': 0.6,
            'curve-style': 'bezier'
          }
        },
        { selector: 'edge.hidden-type', style: { display: 'none' } },
        {
          selector: 'edge.edge-dim',
          style: { 'line-color': FOCUS.dim, 'target-arrow-color': FOCUS.dim, opacity: 0.4 }
        },
        {
          selector: 'edge.edge-focused',
          style: { 'line-color': FOCUS.edge, 'target-arrow-color': FOCUS.edge, width: 2.5, opacity: 1 }
        },
        {
          selector: 'edge.query-context-edge',
          style: { 'line-color': QUERY.edge, 'target-arrow-color': QUERY.edge, width: 2, opacity: 1 }
        },
        {
          selector: 'edge.query-visiting-edge',
          style: { 'line-color': QUERY.searching, 'target-arrow-color': QUERY.searching, width: 2, opacity: 0.9 }
        },
        {
          selector: 'edge.query-dim-edge',
          style: { 'line-color': QUERY.dim, 'target-arrow-color': QUERY.dim, opacity: 0.35 }
        }
      ],
      layout: { name: 'preset' },
      wheelSensitivity: 0.3,
      minZoom: 0.1,
      maxZoom: 4
    });

    this.cy.on('tap', 'node', (evt: EventObject) => {
      this.handleNodeClick((evt.target as NodeSingular).id());
    });

    this.cy.on('tap', (evt: EventObject) => {
      if (evt.target === this.cy) this.handleNodeClick(null);
    });
  }

  private renderGraph() {
    if (!this.cy || !this.graphData) return;

    const elements = [
      ...this.graphData.nodes.map(n => ({
        data: { id: n.id, label: truncate(n.name), name: n.name, type: n.type, color: colorForType(n.type) }
      })),
      ...this.graphData.edges.map(e => ({
        data: { id: e.id, source: e.source, target: e.target }
      }))
    ];

    this.cy.elements().remove();
    this.cy.add(elements);
    this.runLayout();
    this.applyTypeFilter();
  }

  private runLayout() {
    if (!this.cy) return;
    // fcose measures the container's real pixel size when it runs; force a
    // fresh measurement first or a still-settling page layout can collapse
    // every node onto one invisible point (same fix as the Knowledge Graph
    // page's cytoscape setup).
    this.cy.resize();
    this.cy.layout({
      name: 'fcose',
      quality: 'default',
      animate: false,
      fit: true,
      padding: 40,
      nodeRepulsion: 4500,
      idealEdgeLength: 90,
      edgeElasticity: 0.45,
      numIter: 2500
    } as unknown as cytoscape.LayoutOptions).run();
  }

  resetView() {
    this.cy?.fit(undefined, 50);
  }

  zoomIn() {
    this.zoomBy(1.25);
  }

  zoomOut() {
    this.zoomBy(1 / 1.25);
  }

  private zoomBy(factor: number) {
    if (!this.cy) return;
    const level = Math.min(Math.max(this.cy.zoom() * factor, 0.1), 4);
    this.cy.zoom({ level, renderedPosition: { x: this.cy.width() / 2, y: this.cy.height() / 2 } });
  }

  toggleType(type: string) {
    this.activeTypes.update(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
    this.applyTypeFilter();
  }

  selectAllTypes() {
    this.activeTypes.set(new Set(this.typeCounts().map(([t]) => t)));
    this.applyTypeFilter();
  }

  selectNoTypes() {
    this.activeTypes.set(new Set());
    this.applyTypeFilter();
  }

  private applyTypeFilter() {
    if (!this.cy || !this.graphData) return;
    const active = this.activeTypes();
    const hiddenIds = new Set(this.graphData.nodes.filter(n => !active.has(n.type)).map(n => n.id));

    this.cy.nodes().forEach(node => { node.toggleClass('hidden-type', hiddenIds.has(node.id())); });
    this.cy.edges().forEach(edge => {
      const hide = hiddenIds.has(edge.data('source')) || hiddenIds.has(edge.data('target'));
      edge.toggleClass('hidden-type', hide);
    });
  }

  onSearchInput(value: string) {
    this.searchQuery.set(value);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    if (!value.trim() || !this.graphData) {
      this.searchResults.set([]);
      this.searchOpen.set(false);
      return;
    }
    this.searchDebounce = setTimeout(() => {
      this.searchResults.set(this.playgroundService.searchNodes(this.graphData!, value));
      this.searchOpen.set(true);
    }, 120);
  }

  selectSearchResult(node: PlaygroundNode) {
    this.searchQuery.set(node.name);
    this.searchOpen.set(false);
    if (this.queryActive()) this.cancelAsk();
    this.focusNode(node.id);
  }

  closeSearchResults() {
    this.searchOpen.set(false);
  }

  private handleNodeClick(id: string | null) {
    if (this.queryActive()) this.cancelAsk();
    if (id) this.focusNode(id);
    else this.clearFocus();
  }

  focusNode(id: string) {
    const node = this.nodeById(id);
    if (!node || !this.graphData || !this.cy) return;

    this.focusNodeId.set(id);
    this.focusedNode.set(node);

    const outgoing = this.graphData.edges
      .filter(e => e.source === id)
      .map(e => ({ id: e.id, relationship: e.relationship, targetId: e.target, targetName: this.nodeById(e.target)?.name || e.target }));
    const incoming = this.graphData.edges
      .filter(e => e.target === id)
      .map(e => ({ id: e.id, relationship: e.relationship, sourceId: e.source, sourceName: this.nodeById(e.source)?.name || e.source }));
    this.outgoingConnections.set(outgoing);
    this.incomingConnections.set(incoming);

    const neighborIds = new Set<string>([...outgoing.map(o => o.targetId), ...incoming.map(i => i.sourceId)]);
    const touchingEdgeIds = new Set<string>([...outgoing.map(o => o.id), ...incoming.map(i => i.id)]);

    this.cy.nodes().forEach(n => {
      n.removeClass('node-dim node-focused node-neighbor');
      if (n.id() === id) n.addClass('node-focused');
      else if (neighborIds.has(n.id())) n.addClass('node-neighbor');
      else n.addClass('node-dim');
    });
    this.cy.edges().forEach(e => {
      e.removeClass('edge-dim edge-focused');
      e.addClass(touchingEdgeIds.has(e.id()) ? 'edge-focused' : 'edge-dim');
    });

    const target = this.cy.nodes().filter(n => n.id() === id || neighborIds.has(n.id()));
    this.cy.animate({ fit: { eles: target, padding: 60 }, duration: 400 });
  }

  clearFocus() {
    this.focusNodeId.set(null);
    this.focusedNode.set(null);
    this.outgoingConnections.set([]);
    this.incomingConnections.set([]);
    this.cy?.nodes().removeClass('node-dim node-focused node-neighbor');
    this.cy?.edges().removeClass('edge-dim edge-focused');
  }

  private resetQueryStyles() {
    this.cy?.nodes().removeClass('query-seed query-context query-visiting query-dim');
    this.cy?.edges().removeClass('query-context-edge query-visiting-edge query-dim-edge');
  }

  askGraph() {
    const question = this.question().trim();
    if (!question || this.askBusy() || !this.graphData) return;

    const token = ++this.askRequestToken;
    this.stopAskAnimation();
    this.clearFocus();
    this.resetQueryStyles();
    this.queryActive.set(true);
    this.askAnswer.set('');
    this.askError.set(null);
    this.askIsLocalPreview.set(false);
    this.askStatus.set('searching');

    const seedIds = this.playgroundService.findSeedNodes(this.graphData, question);
    if (seedIds.length === 0) {
      this.askAnswer.set('No entities in the graph matched that question. Try naming something specific from the graph.');
      this.askStatus.set('done');
      this.queryActive.set(false);
      return;
    }

    const { order, nodeIds, edgeIds } = this.playgroundService.traverse(this.graphData, seedIds);

    // Animate the BFS visiting the same way the POC did, then ask the
    // backend for the answer once the context is settled — the traversal
    // itself needs no secret, only the natural-language answer does.
    order.forEach((step, i) => {
      const t = setTimeout(() => this.markVisiting(step.nodeId, step.edgeId), i * 35);
      this.askTimers.push(t);
    });

    const finish = setTimeout(() => {
      if (token !== this.askRequestToken || !this.graphData) return;
      this.setQueryContext(nodeIds, edgeIds, seedIds);
      this.askStatus.set('answering');

      const facts = this.playgroundService.buildFacts(this.graphData, nodeIds, 40);
      this.playgroundService.askBackend(question, facts).subscribe({
        next: (res) => {
          if (token !== this.askRequestToken) return;
          this.askAnswer.set(res.answer || 'The answer service returned an empty response.');
          this.askStatus.set('done');
          this.queryActive.set(false);
        },
        error: () => {
          if (token !== this.askRequestToken) return;
          this.askIsLocalPreview.set(true);
          this.askAnswer.set(
            `No AI answer service is connected right now, so here's what the graph search surfaced (${nodeIds.length} nodes, ${edgeIds.length} edges):\n\n` +
            facts.slice(0, 12).map(f => `• ${f}`).join('\n')
          );
          this.askStatus.set('done');
          this.queryActive.set(false);
        }
      });
    }, order.length * 35 + 50);
    this.askTimers.push(finish);
  }

  private markVisiting(nodeId: string, edgeId: string | null) {
    this.cy?.getElementById(nodeId)?.addClass('query-visiting');
    if (edgeId) this.cy?.getElementById(edgeId)?.addClass('query-visiting-edge');
  }

  private setQueryContext(nodeIds: string[], edgeIds: string[], seedIds: string[]) {
    if (!this.cy) return;
    const nodeIdSet = new Set(nodeIds);
    const edgeIdSet = new Set(edgeIds);
    const seedIdSet = new Set(seedIds);

    this.cy.nodes().forEach(n => {
      n.removeClass('query-visiting query-seed query-context query-dim');
      if (seedIdSet.has(n.id())) n.addClass('query-seed');
      else if (nodeIdSet.has(n.id())) n.addClass('query-context');
      else n.addClass('query-dim');
    });
    this.cy.edges().forEach(e => {
      e.removeClass('query-visiting-edge query-context-edge query-dim-edge');
      e.addClass(edgeIdSet.has(e.id()) ? 'query-context-edge' : 'query-dim-edge');
    });

    const target = this.cy.nodes().filter(n => nodeIdSet.has(n.id()) || seedIdSet.has(n.id()));
    if (target.length) this.cy.animate({ fit: { eles: target, padding: 60 }, duration: 400 });
  }

  clearAsk() {
    this.cancelAsk();
    this.askStatus.set('idle');
    this.askAnswer.set('');
    this.askError.set(null);
    this.askIsLocalPreview.set(false);
    this.question.set('');
    this.resetQueryStyles();
  }

  /** Invalidates any in-flight animation/backend call without touching the
   * answer currently on screen — used when the user clicks elsewhere while
   * a question is still resolving. */
  private cancelAsk() {
    this.askRequestToken++;
    this.stopAskAnimation();
    this.queryActive.set(false);
  }

  private stopAskAnimation() {
    this.askTimers.forEach(t => clearTimeout(t));
    this.askTimers = [];
  }
}
